/* Copyright (c) 2021-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useState } from "react";
import {
  Resource,
  ResourcesSortFields,
  SortOrder,
  useGetResourcesLazyQuery,
} from "~/generated/graphql";
import { ColumnType, SelectionMethod } from "~/types";

export type SidebarItem = {
  name: string;
  id: string;
  tag: SelectionMethod;
  columns: {
    data: string[];
    type: ColumnType;
  }[];
};

export function useGetResources() {
  const [getData, { data: resourcesData, loading, error, fetchMore, refetch }] =
    useGetResourcesLazyQuery({
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-only",
    });

  const [resources, setResources] = useState<Resource[]>([]);

  const getResources = useCallback(
    () =>
      getData({
        variables: {
          page: 1,
          limit: 50,
          sort: {
            field: ResourcesSortFields.Name,
            order: SortOrder.Asc,
          },
        },
      }),
    [getData]
  );

  const count = resourcesData?.resources?.pageInfo.count ?? 0;
  const pages = resourcesData?.resources?.pageInfo?.pages ?? 0;

  useEffect(() => {
    const queryResult = resourcesData?.resources?.result ?? [];
    setResources(queryResult as Resource[]);
    if (!loading && count > queryResult.length) {
      [...Array(pages).keys()].slice(1).forEach((page) => {
        fetchMore?.({
          variables: {
            page: page + 1,
            limit: 50,
            sort: {
              field: ResourcesSortFields.Name,
              order: SortOrder.Asc,
            },
          },
        }).then(({ data: fetchedData }) => {
          return setResources((prev: Resource[]) => [
            ...prev,
            ...(fetchedData.resources?.result as Resource[]),
          ]);
        });
      });
    }
  }, [count, fetchMore, pages, resourcesData?.resources?.result, setResources]);
  return {
    getResources,
    resources,
    loading,
    refetch,
    error,
  } as const;
}
